import * as React from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'

import SpinnerWrapper from '@frontrow/console/src/components/common/spinner-wrapper'
import {postJobs} from '@frontrow/console/src/helpers/api-jobs'
import JobsForm from '@frontrow/console/src/components/pages/jobs-page/jobs-form/jobs-form'
import {type ValidatedT} from '@frontrow/console/src/components/pages/jobs-page/jobs-form/validated'
import {
  valid,
  invalid,
  empty,
  extend,
  lastValidValue,
  toMaybe
} from '@frontrow/console/src/components/pages/jobs-page/jobs-form/validated'

type ActionT =
  // Update json text area
  // Schedule job for later
  | {
      tag: 'change-text'
      text: string
    } // Toggle retries checkbox
  | {
      tag: 'change-date-time'
      dateTime: string
    } // Toggle schedule checkbox
  | {
      tag: 'toggle-retries'
    } // Post jobs to queue
  | {
      tag: 'toggle-schedule'
    } // Post succeeded
  | {
      tag: 'spawn-jobs'
    } // Post failed
  | {
      tag: 'success'
      message: string
    } // Clear success message
  | {
      tag: 'error'
      error: string
    }
  | {
      tag: 'clear'
    }

type StateT = {
  jsonText: string
  validatedJobs: ValidatedT<Array<unknown>>
  retryFailedJobs: boolean
  scheduleForLater: boolean
  validatedDateTime: ValidatedT<Date>
  isLoading: boolean
  error: string | undefined | null
  message: string | undefined | null
}

export default function JobsFormContainer(): React.ReactElement {
  const [state, setState] = React.useState<StateT>({
    jsonText: '',
    validatedJobs: empty(),
    retryFailedJobs: false,
    scheduleForLater: false,
    validatedDateTime: empty(),
    isLoading: false,
    error: null,
    message: null
  })

  const onToggleRetryFailedJobs = () => {
    setState(reduce({tag: 'toggle-retries'}))
  }

  const onToggleScheduleForLater = () => {
    setState(reduce({tag: 'toggle-schedule'}))
  }

  const onChangeDateTime = (dateTime: string) => {
    setState(reduce({tag: 'change-date-time', dateTime}))
  }

  const onChangeJsonText = (text: string) => {
    setState(reduce({tag: 'change-text', text}))
  }

  const clear = () => {
    setTimeout(() => setState(reduce({tag: 'clear'})), 3000)
  }

  const spawnJobs = (
    jobs: Array<unknown>,
    retryFailedJobs: boolean,
    scheduleAt?: Date | null
  ): Promise<unknown> => {
    return postJobs(jobs, {retryFailedJobs, scheduleAt}).then(
      () => {
        const message = successMessage(jobs)
        setState(reduce({tag: 'success', message}))
        clear()
      },
      xhr => {
        const error = firstResponseApiError(xhr).message
        setState(reduce({tag: 'error', error}))
      }
    )
  }

  const onSubmit = () => {
    const {isLoading, validatedJobs, retryFailedJobs, validatedDateTime} = state
    if (isLoading) {
      return
    }

    switch (validatedJobs.tag) {
      case 'empty':
        break
      case 'invalid':
        break
      case 'valid':
        setState(reduce({tag: 'spawn-jobs'}))
        spawnJobs(validatedJobs.value, retryFailedJobs, toMaybe(validatedDateTime))
        break
      default:
        exhaustive(validatedJobs as never, 'ValidatedT')
        break
    }
  }

  const {
    isLoading,
    message,
    error,
    jsonText,
    validatedJobs,
    retryFailedJobs,
    scheduleForLater,
    validatedDateTime
  } = state

  const validJson = validatedJobs.tag === 'valid'
  const validDateTime = !scheduleForLater || validatedDateTime.tag === 'valid'

  return (
    <SpinnerWrapper isLoading={isLoading}>
      <JobsForm
        canSubmit={!isLoading && validJson && validDateTime}
        message={message}
        error={error}
        jsonError={validatedJobs.tag === 'invalid' ? validatedJobs.message : null}
        jsonText={jsonText}
        retryFailedJobs={retryFailedJobs}
        scheduleForLater={scheduleForLater}
        dateTime={lastValidValue(validatedDateTime) ?? undefined}
        validDateTime={validDateTime}
        onChangeJsonText={onChangeJsonText}
        onToggleRetryFailedJobs={onToggleRetryFailedJobs}
        onToggleScheduleForLater={onToggleScheduleForLater}
        onChangeDateTime={onChangeDateTime}
        onSubmit={onSubmit}
      />
    </SpinnerWrapper>
  )
}

type ValidatedJobsT = ValidatedT<Array<unknown>>

function validateJobsJson(text: string): ValidatedJobsT {
  if (text.trim() === '') {
    return empty()
  }

  let jobs
  try {
    jobs = JSON.parse(text)
  } catch (e) {
    const error = e as {name: string; message: string}
    return invalid(`${error.name}: ${error.message}`)
  }

  return validateJobsArray(jobs)
}

function validateJobsArray(jobs: unknown): ValidatedJobsT {
  if (jobs === null || jobs === undefined) {
    return invalid('Payload must not be null or undefined')
  }

  if (!Array.isArray(jobs)) {
    return invalid('Payload must be a non-empty array of objects')
  }

  let i
  for (i = 0; i < jobs.length; ++i) {
    const job = jobs[i]
    if (job === null) {
      return invalid(`Object at index ${i} must not be null`)
    }
    if (Array.isArray(job) || !(job instanceof Object)) {
      return invalid(`Value at index ${i} must be an object`)
    }
  }

  if (i === 0) {
    return invalid('Payload must be non-empty array of objects')
  }

  return valid(jobs)
}

function successMessage(jobs: Array<unknown>): string {
  const numJobs = jobs.length
  return numJobs === 1 ? 'Successfully spawned job' : `Successfully spawned ${numJobs} jobs`
}

function validateDateTime(dateTime: string, previous: ValidatedT<Date>): ValidatedT<Date> {
  if (dateTime === '') {
    return empty()
  }
  const date = new Date(dateTime)
  if (isNaN(date.getTime())) {
    return extend(`Malformed date-time: ${dateTime}`, previous)
  }
  return valid(date)
}

function reduce(action: ActionT): (state: StateT) => StateT {
  return state => {
    switch (action.tag) {
      case 'change-text':
        return {
          ...state,
          message: null,
          error: null,
          jsonText: action.text,
          validatedJobs: validateJobsJson(action.text)
        }
      case 'change-date-time':
        return {
          ...state,
          message: null,
          error: null,
          validatedDateTime: validateDateTime(action.dateTime, state.validatedDateTime)
        }
      case 'toggle-retries':
        return {
          ...state,
          message: null,
          error: null,
          retryFailedJobs: !state.retryFailedJobs
        }
      case 'toggle-schedule':
        const scheduleForLater = !state.scheduleForLater
        return {
          ...state,
          message: null,
          error: null,
          scheduleForLater,
          validatedDateTime: scheduleForLater ? valid(new Date()) : empty()
        }
      case 'spawn-jobs':
        return {
          ...state,
          isLoading: true,
          message: null,
          error: null
        }
      case 'success':
        return {
          ...state,
          jsonText: '',
          validatedJobs: empty(),
          message: action.message,
          error: null,
          isLoading: false
        }
      case 'clear':
        return {
          ...state,
          message: null
        }
      case 'error':
        return {
          ...state,
          message: null,
          error: action.error,
          isLoading: false
        }
      default:
        return exhaustive(action as never, 'Action')
    }
  }
}
